import { Button } from "@/components/tiptapNovel/ui/button";
import { EditorBubbleItem, type EditorInstance } from "novel";
import {
  FoldVerticalIcon,
  CirclePlayIcon,
  UnfoldVerticalIcon,
  RefreshCcwIcon,
  Loader2Icon,
} from "lucide-react";
import { useState } from "react";
import { message } from "antd";
import { PromptType, CoreEntityMap, CoreEntityType } from "#/index";
import { usePromptsByType } from "@/stores/novelCoreStore";
import { assertCanGenerate, deductAfterGenerate, estimateTokensFromMessages } from "@/lib/thesisQuota";
import { applyMarkdownInRange } from "@/lib/applyMarkdownInEditor";
import { thesisApiBase, THESIS_MODEL_NAME } from "@/lib/thesisPlatform";
import { AIServiceType, aiProviderFactory } from "@/ai";
import { assertLiveChapterAccess } from "@/lib/thesisFramework/livePrompt";
import { useSubscriptionStore } from "@/stores/subscriptionStore";

const ACTION_HINT: Record<string, string> = {
  扩写: "扩写选中文字：展开论述，保持论点与术语一致。",
  改写: "改写选中文字：优化学术表述，长度与原文接近。",
  缩写: "缩写选中文字：压缩篇幅，输出必须比原文短。",
};

const CTX_CHARS = 400
/** 缩写至少要有可压空间；太短时模型只会「润成完整句」反而变长 */
const MIN_CONDENSE_CHARS = 40

/** 取选区前后文，供模型衔接；不会写入稿面 */
function selectionContext(editor: EditorInstance, from: number, to: number) {
  const docSize = editor.state.doc.content.size
  const beforeFrom = Math.max(1, from - CTX_CHARS)
  const afterTo = Math.min(docSize, to + CTX_CHARS)
  const before = editor.state.doc.textBetween(beforeFrom, from, "\n").trim()
  const after = editor.state.doc.textBetween(to, afterTo, "\n").trim()
  return { before, after }
}

/** 选区编辑用轻量 system：勿套整章写作 prompt，否则模型会当「继续写论文」越写越长 */
function selectionActionSystem(name: string, selectedLen: number): string {
  const shared = [
    "你是 MBA 论文学术编辑，只做选区局部替换。",
    "【上文】【下文】仅供语气/术语衔接，禁止复述、禁止并入输出。",
    "只输出替换后的正文：不要解释、不要标题、不要引号包裹。",
  ].join("\n")

  if (name === "缩写") {
    const target = Math.max(8, Math.floor(selectedLen * 0.5))
    const max = Math.max(target, Math.floor(selectedLen * 0.7))
    return [
      shared,
      `【本次唯一任务：缩写】原文 ${selectedLen} 字。`,
      `- 删除冗余，保留核心论点与关键证据口径。`,
      `- 输出必须严格短于原文；目标约 ${target} 字，硬上限 ${max} 字。`,
      `- 禁止补充新论据、新例子、新背景、新过渡句。`,
    ].join("\n")
  }
  if (name === "扩写") {
    const target = Math.floor(selectedLen * 1.8)
    const max = Math.floor(selectedLen * 2.5)
    return [
      shared,
      `【本次唯一任务：扩写】原文 ${selectedLen} 字。`,
      `- 展开论述层次，保持原论点与术语。`,
      `- 目标约 ${target} 字，不超过 ${max} 字；勿另起无关段落。`,
    ].join("\n")
  }
  // 改写（及未知动作）
  const min = Math.floor(selectedLen * 0.8)
  const max = Math.ceil(selectedLen * 1.25)
  return [
    shared,
    `【本次唯一任务：改写】原文 ${selectedLen} 字。`,
    `- 优化表述与逻辑连贯，不改变核心论点。`,
    `- 篇幅与原文接近（约 ${min}–${max} 字），禁止明显变长或变短。`,
    `- 禁止新增论据或背景。`,
  ].join("\n")
}

/** 把纯文本变成可插入的节点（双换行分段） */
function textToContent(text: string) {
  const paras = text.split(/\n+/).map((p) => p.trimEnd()).filter((p, i, arr) => p.length > 0 || i === arr.length - 1)
  if (paras.length <= 1) return text
  return paras.map((p) => ({ type: "paragraph", content: p ? [{ type: "text", text: p }] : [] }))
}

/** 选中文字 → 扩写/改写/缩写：原文保留至首包；之后整段覆盖「改写区」，不追加混写 */
const GenerateSelector = () => {
  const prompts = usePromptsByType(PromptType.ManuscriptReplacement);
  const [busyName, setBusyName] = useState<string | null>(null)

  const runReplace = async (editor: EditorInstance, prompt: CoreEntityMap<CoreEntityType.Prompts>) => {
    if (busyName) return
    const { from, to, empty } = editor.state.selection
    if (empty || from === to) {
      message.warning("请先选中要处理的文字")
      return
    }
    const selected = editor.state.doc.textBetween(from, to, "\n").trim()
    if (!selected) {
      message.warning("选区没有文字")
      return
    }
    if (!assertCanGenerate()) return
    const access = assertLiveChapterAccess()
    if (!access.ok) {
      message.warning(access.message)
      return
    }
    const base = thesisApiBase()
    if (!base) {
      message.error("未配置代付 API")
      return
    }

    const name = prompt.attributes.name
    if (name === "缩写" && selected.length < MIN_CONDENSE_CHARS) {
      message.warning(`选区太短（${selected.length} 字），缩写请至少选一段完整表述`)
      return
    }
    const { before, after } = selectionContext(editor, from, to)
    const messages = [
      {
        role: "system" as const,
        content: selectionActionSystem(name, selected.length),
      },
      {
        role: "user" as const,
        content: [
          before ? `【上文】\n${before}\n` : "",
          `【待处理文本｜${selected.length}字｜动作：${name}】\n${selected}`,
          after ? `\n【下文】\n${after}` : "",
        ]
          .filter(Boolean)
          .join("\n"),
      },
    ]

    setBusyName(name)
    // 改写区：[rangeFrom, rangeTo)；首包前仍是原文，首包起整段换成已生成全文
    let rangeFrom = from
    let rangeTo = to
    let out = ""
    let dirty = false
    let raf = 0
    const hideWait = message.loading(`正在${name}…`, 0)

    const paint = () => {
      raf = 0
      if (!out) return
      const content = textToContent(out)
      editor
        .chain()
        .focus()
        .insertContentAt({ from: rangeFrom, to: rangeTo }, content)
        .run()
      // 插入后光标在新内容末尾 → 下一帧只覆盖「已生成区」
      rangeTo = editor.state.selection.to
      dirty = true
      hideWait()
    }

    try {
      const jwt = useSubscriptionStore.getState().token
      const provider = aiProviderFactory.createProvider({
        aiServiceType: AIServiceType.OpenAI,
        baseUrl: `${base}/v1`,
        apiKey: jwt || "thesis-platform",
      })
      await provider.sendStreamingRequest(
        { model: THESIS_MODEL_NAME, messages },
        (chunk) => {
          if (!chunk.text) return
          out += chunk.text
          dirty = true
          if (!raf) raf = requestAnimationFrame(paint)
        },
      )
      if (raf) {
        cancelAnimationFrame(raf)
        paint()
      } else if (dirty && out) {
        paint()
      }
      hideWait()
      if (!out.trim()) {
        message.error(`${name}结果为空，原文未改动`)
        return
      }
      // 流式用纯文本；结束后统一按 Markdown 渲染进选区
      applyMarkdownInRange(editor, rangeFrom, rangeTo)
      deductAfterGenerate({
        promptTokens: estimateTokensFromMessages(messages),
        completionText: out,
      })
      message.success(`已${name}`, 1.5)
    } catch (e) {
      if (raf) cancelAnimationFrame(raf)
      hideWait()
      message.error(e instanceof Error ? e.message : `${name}失败`)
    } finally {
      setBusyName(null)
    }
  }

  return (
    <div className="flex gap-0.5 empty:hidden">
      {prompts.map((prompt) => {
        const name = prompt.attributes.name
        const loading = busyName === name
        return (
          <EditorBubbleItem
            key={prompt.id}
            className="flex px-0.5 py-0.5 bg-primary rounded pointer-events-auto"
            onSelect={(editor) => {
              void runReplace(editor, prompt)
            }}
          >
            <Button
              className="flex items-center gap-2"
              title={ACTION_HINT[name] || name}
              size="sm"
              variant="default"
              disabled={!!busyName}
            >
              {loading ? (
                <Loader2Icon className="w-4 h-4 animate-spin" />
              ) : (
                <GenerateIcon prompt={prompt} />
              )}
              <span className="text-sm/none">{name}</span>
            </Button>
          </EditorBubbleItem>
        )
      })}
    </div>
  )
}

const GenerateIcon = ({ prompt }: { prompt: CoreEntityMap<CoreEntityType.Prompts> }) => {
  if (prompt.attributes.name === "扩写") {
    return <UnfoldVerticalIcon className="w-4 h-4" />
  }
  if (prompt.attributes.name === "改写") {
    return <RefreshCcwIcon className="w-4 h-4" />
  }
  if (prompt.attributes.name === "缩写") {
    return <FoldVerticalIcon className="w-4 h-4" />
  }
  return <CirclePlayIcon className="w-4 h-4" />
}

export default GenerateSelector
